import React from 'react';
import Icon from '../../../components/AppIcon';
import { getEventTypeStyle, getEstadoStyle } from './AdvancedFilters';

const AgendaView = ({ eventos, empleados, onEventClick }) => {
  const formatDate = (fecha) => {
    if (!fecha) return '-';
    const d = new Date(fecha + 'T00:00:00');
    return d?.toLocaleDateString('es-ES', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  };

  const getEmpleadoNombre = (empleadoId, empleados) => {
    const emp = empleados?.find(e => e?.id === empleadoId);
    return emp?.nombre || '-';
  };

  // Group by date
  const grouped = {};
  const sorted = [...(eventos || [])]?.sort((a, b) => {
    const dateCompare = (a?.fecha || '')?.localeCompare(b?.fecha || '');
    if (dateCompare !== 0) return dateCompare;
    return (a?.horaInicio || '')?.localeCompare(b?.horaInicio || '');
  });

  sorted?.forEach(ev => {
    const key = ev?.fecha || 'Sin fecha';
    if (!grouped?.[key]) grouped[key] = [];
    grouped?.[key]?.push(ev);
  });

  const today = new Date()?.toISOString()?.split('T')?.[0];

  if (sorted?.length === 0) {
    return (
      <div className="bg-[#1A1A1A] rounded-xl border border-white/8 p-12 text-center">
        <Icon name="CalendarX" size={48} className="mx-auto mb-4" style={{ color: '#ffffff30' }} />
        <p className="font-oranienbaum text-white/40 text-lg">No hay eventos para mostrar</p>
        <p className="font-oranienbaum text-white/25 text-sm mt-1">Ajusta los filtros o crea un nuevo evento</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {Object.entries(grouped)?.map(([fecha, evs]) => {
        const isToday = fecha === today;
        const isPast = fecha < today;
        return (
          <div key={fecha} className="bg-[#1A1A1A] rounded-xl border border-white/8 overflow-hidden">
            {/* Date header */}
            <div className={`px-4 py-3 flex items-center gap-3 border-b border-white/8 ${isToday ? 'bg-[#D6EF4E]/10' : 'bg-white/3'}`}>
              <div className={`w-2 h-2 rounded-full flex-shrink-0 ${isToday ? 'bg-[#D6EF4E]' : isPast ? 'bg-white/20' : 'bg-[#3A7F6B]'}`} />
              <span className={`font-oranienbaum font-semibold text-sm capitalize ${isToday ? 'text-[#D6EF4E]' : isPast ? 'text-white/40' : 'text-white/80'}`}>
                {formatDate(fecha)}
                {isToday && <span className="ml-2 text-xs bg-[#D6EF4E]/20 text-[#D6EF4E] px-2 py-0.5 rounded-full">HOY</span>}
              </span>
              <span className="ml-auto text-xs text-white/30 font-oranienbaum">{evs?.length} evento{evs?.length !== 1 ? 's' : ''}</span>
            </div>
            {/* Events list */}
            <div className="divide-y divide-white/5">
              {evs?.map(ev => {
                const typeStyle = getEventTypeStyle(ev?.tipoServicio);
                const estadoStyle = getEstadoStyle(ev?.estado);
                return (
                  <div key={ev?.id} onClick={() => onEventClick(ev)}
                    className="px-4 py-3 hover:bg-white/3 cursor-pointer transition-all duration-150 group">
                    <div className="flex items-start gap-3">
                      {/* Time */}
                      <div className="w-16 flex-shrink-0 text-right">
                        <span className="text-xs font-oranienbaum text-white/50">{ev?.horaInicio || '--:--'}</span>
                      </div>

                      {/* Color bar */}
                      <div className="w-1 self-stretch rounded-full flex-shrink-0" style={{ background: typeStyle?.color }} />

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2 flex-wrap">
                          <div>
                            <p className="font-oranienbaum font-semibold text-white text-sm group-hover:text-[#D6EF4E] transition-colors">
                              {ev?.cliente || 'Sin cliente'}
                            </p>
                            <p className="text-xs text-white/40 font-oranienbaum mt-0.5">
                              {ev?.ubicacion || 'Sin ubicación'} {ev?.descripcionPiezas ? `· ${ev?.descripcionPiezas}` : ''}
                            </p>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0 flex-wrap">
                            <span className="text-xs font-oranienbaum px-2 py-0.5 rounded-full border" style={{ color: typeStyle?.color, borderColor: `${typeStyle?.color}40`, background: typeStyle?.bg }}>
                              {ev?.tipoServicio || 'Sin tipo'}
                            </span>
                            <span className="text-xs font-oranienbaum px-2 py-0.5 rounded-full border" style={{ color: estadoStyle?.color, borderColor: `${estadoStyle?.color}40`, background: estadoStyle?.bg }}>
                              {ev?.estado || 'Sin estado'}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-4 mt-1.5 flex-wrap">
                          <span className="text-xs text-white/35 font-oranienbaum flex items-center gap-1">
                            <Icon name="User" size={11} />
                            {getEmpleadoNombre(ev?.empleadoId, empleados)}
                          </span>
                          {ev?.precioServicio && (
                            <span className="text-xs font-oranienbaum font-semibold" style={{ color: '#D6EF4E' }}>
                              {parseFloat(ev?.precioServicio)?.toLocaleString('es-ES', { minimumFractionDigits: 2 })} €
                            </span>
                          )}
                          {ev?.numPiezas && (
                            <span className="text-xs text-white/35 font-oranienbaum flex items-center gap-1">
                              <Icon name="Package" size={11} />
                              {ev?.numPiezas} kg
                            </span>
                          )}
                        </div>
                      </div>

                      <Icon name="ChevronRight" size={14} className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: '#D6EF4E' }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default AgendaView;
